<template>
  <div class="tb-navbar">
    <div class="production-info" :class="{ editing }">
      <div class="preview-controls">
        <div class="flex content-center items-center">
          <dropdown position="bottom left" class="screen-size__dropdown mr-4">
            <template #trigger>
              <button class="tb-btn tb-btn-text sm medium-em header-section" data-test="production-screenSize-dropdown">{{ selectedScreenSize }} <span class="fa fa-chevron-down"></span></button>
            </template>
            <template #dropdown-body>
              <ul class="screen-size__options-list">
                <li
                  v-for="size in presetDimensions"
                  :key="size.label"
                >
                  <button
                    class="tb-btn tb-btn-text sm w-full text-left"
                    :data-test="`production-screenSize-${!size.short ? size.label : size.short}`"
                    @click="usePresetDimensions(size)"
                  >
                    {{ size.label }}
                  </button>
                </li>
              </ul>
            </template>
          </dropdown>
          <div class="flex items-center">
            <label class="mb-0 mr-1">
              <input
                type="number"
                v-model.number.lazy="iframeWidth"
                min="1"
                class="screen-size__input px-[5px] py-[1px]"
                data-test="production-screenSize-width"
                :disabled="disableSizeInputs"
              >
            </label>
            <span>x</span>
            <label class="mb-0 ml-1 mr-2">
              <input
                type="number"
                v-model.number.lazy="iframeHeight"
                min="1"
                class="screen-size__input px-[5px] py-[1px]"
                data-test="production-screenSize-height"
                :disabled="disableSizeInputs"
              >
            </label>
            <dropdown
              position="bottom left"
              class="mr-4 backgroundDropdown"
            >
              <template #trigger>
                <div class="flex items-center">
                  <div
                    v-if="['color', 'none'].includes(productionBackgroundType)"
                    :class="{
                      outputBackgroundColor: true,
                      'pointer-events-none': productionBackgroundType === 'none',
                    }"
                    data-test="production-background-value"
                  >
                    <colorpicker
                      :color="productionBackgroundType === 'color' ? pendingBackgroundColor || backgroundColor : 'rgba(0,0,0,0)'"
                      @change="setBackgroundColor"
                      :show-buttons="true"
                      @save="saveBackgroundColor"
                      @cancel="cancelBackgroundColor"
                    />
                  </div>
                  <div v-else class="outputBackground" data-test="production-background-value">
                    <div
                      class="outputBackground__image"
                      :style="{
                        backgroundImage: productionBackgroundMedia,
                      }"
                    ></div>

                    <button
                      class="tb-btn tb-btn-contained on-image outputBackground__edit"
                      data-entity="producer-changeBackground"
                      data-test="production-background-changeMedia"
                      @click.stop="selectAsset"
                    >
                      <span class="fas fa-pencil-alt"></span>
                    </button>
                  </div>

                  <button
                    class="tb-btn tb-btn-text sm medium-em header-section"
                    data-entity="producer-backgroundDropdown"
                    data-test="production-background-dropdown"
                  >
                    <span class="fa fa-chevron-down"></span>
                  </button>
                </div>
              </template>
              <template #dropdown-body>
                <ul class="screen-size__options-list">
                  <li
                    v-for="o in backgroundOptions"
                    :key="o.value"
                  >
                    <button
                      class="tb-btn tb-btn-text sm w-full text-left flex items-center content-between"
                      :data-entity="`producer-backgroundDropdownOption-${ o.value }`"
                      :data-test="`production-background-${ o.value }`"
                      @click="productionBackgroundType = o.value"
                    >
                      <span>{{ o.label }}</span>
                      <span
                        v-if="productionBackgroundType === o.value"
                        class="fa fa-check ml-2"
                        :data-test="`production-background-${ o.value }-selected`"
                      ></span>
                    </button>
                  </li>
                </ul>
              </template>
            </dropdown>
          </div>
        </div>
      </div>

      <div class="production-title-wrapper">
        <p class="production-title-wrapper__title" data-test="production-title">{{ production.title }}</p>

        <button class="tb-btn tb-btn-text" data-test="production-title-edit" @click="toggleUpdateProductionModal">
          <i class="fas fa-pencil"></i>
        </button>
      </div>
    </div>

    <div class="navbar-tools">
      <AvatarList :users="connectedUsers" />

      <span class="navbar-separator">|</span>

      <button
        class="tb-btn tb-btn-contained medium-em"
        name="Manage Output"
        data-test="production-outputs-button"
        @click="toggleScreensModal"
      >
        <span class="fas fa-desktop mr-1"></span>
        <span>Outputs</span>
      </button>

      <button
        id="timelineSettingsButton"
        class="tb-btn tb-btn-contained medium-em timeline-settings-button"
        title="Timeline settings"
        aria-label="Timeline settings"
        data-entity="producer-timeline-openSettings"
        @click="showSettingsModal = true"
      >
        <span class="fas fa-cog" />
      </button>

      <button
        v-if="showSaveButton"
        class="tb-btn tb-btn-contained high-em"
        name="Save Production"
        data-test="production-save-button"
        :disabled="isSaving"
        @click="saveProduction"
      >
        <span class="fas fa-save mr-1"></span>

        <span>
          {{ isSaving ? 'Saving Production...' : 'Save Production' }}
        </span>
      </button>
    </div>

    <modal
      v-if="showSettingsModal"
      class="producer-settings-modal"
      @close="showSettingsModal = false"
    >
      <template #header>
        <div class="title">Production Options</div>
        <button
          class="modal-close tb-btn tb-btn-text sm low-em"
          aria-label="Close production options"
          data-entity="producer-timeline-closeSettings"
          @click="showSettingsModal = false"
        >
          <span class="fas fa-times" />
        </button>
      </template>

      <div class="producer-settings__field">
        <div class="producer-settings__label">
          Custom Graphic Transition Mode
        </div>

        <div class="producer-settings__description">
          This controls how Custom Graphics should animate in and out when moving between them on the timeline. Out/In waits for the current Custom Graphic to leave before showing the new ones (default). Overlap will start showing the new Custom Graphic right away.
        </div>

        <select
          class="tb-select sm"
          v-model="transitionMode"
          title="Transition mode used when switching between graphics"
          data-entity="producer-timeline-transitionMode"
          @change="setTransitionMode"
        >
          <option value="out-in">Out/In</option>
          <option value="overlap">Overlap</option>
        </select>
      </div>

      <hr />

      <div class="producer-settings__field">
        <div class="producer-settings__label">
          Social Transition Mode
        </div>

        <div class="producer-settings__description">
          This controls how Social should animate in and out when moving between them on the timeline. Cut will immediately hide the current Social Graphic and switch to the new one. Fade will slowly fade in the new Social Graphic over the current one.
        </div>

        <select
          class="tb-select sm"
          v-model="transition"
          title="Transition mode used when switching between Social Graphics"
          data-entity="producer-timeline-transition"
          @change="setTransition"
        >
          <option value="animated cutIn">Cut</option>
          <option value="animated fadeIn">Fade</option>
        </select>
      </div>
    </modal>
  </div>
</template>

<script>
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import relativeTime from 'dayjs/plugin/relativeTime';
import { mapState, mapActions } from 'vuex';

import ConduitEventsService from '@/js/services/conduitEventsService';
import getResizedImageUrl from '@/js/utils/imageUrl';
import AvatarList from '../user-avatar/AvatarList.vue';
import Dropdown from '../../shared/dropdown/Dropdown.vue';
import Modal from '../modal/Modal.vue';

dayjs.extend(duration);
dayjs.extend(relativeTime);

export default {
  components: {
    AvatarList,
    Dropdown,
    Modal,
  },

  data() {
    return {
      // Storing current date in state so we can create a ticker
      now: dayjs(),
      interval: null,
      editing: false,
      showSettingsModal: false,
      showSaveButton: this.$ld.has('producer-save-production-button', false),
      useCustomDimensions: false,
      pendingBackgroundColor: null,
      presetDimensions: [
        {
          label: '16:9 - 1920x1080',
          short: '16:9',
          width: 1920,
          height: 1080,
        },
        {
          label: '4:3 - 1280x960',
          short: '4:3',
          width: 1280,
          height: 960,
        },
        {
          label: '9:16 - 1080x1920',
          short: '9:16',
          width: 1080,
          height: 1920,
        },
        {
          label: '3:4 - 960x1280',
          short: '3:4',
          width: 960,
          height: 1280,
        },
        {
          label: 'Custom',
        },
      ],
      backgroundOptions: [
        {
          label: 'Transparent',
          value: 'none',
        },
        {
          label: 'Chroma Color',
          value: 'color',
        },
        {
          label: 'Image',
          value: 'image',
        },
        {
          label: 'Video',
          value: 'video',
        },
      ],
    };
  },

  mounted() {
    $socket.emit('getClients', {
      production_id: this.production?._id,
    });

    this.interval = setInterval(() => {
      this.now = dayjs();
    }, 30e3);
  },

  created() {
    this.$ld.on(this.onChangeFlags); // TODO: Remove
  },

  beforeUnmount() {
    this.$ld.off(this.onChangeFlags); // TODO: Remove
  },

  unmounted() {
    clearInterval(this.interval);
  },

  methods: {
    ...mapActions([
      'updateDimensions',
      'toggleScreensModal',
      'toggleUpdateProductionModal',
      'sendSettings',
      'sendPreviewSettings',
      'setBackgroundType',
      'setBackgroundAsset',
      'saveProduction',
    ]),

    setTransitionMode(e) {
      this.$store.dispatch('sendSettings');
      this.$store.dispatch('sendPreviewSettings');

      ConduitEventsService.track(
          'producer.changed.transition-mode',
          {
            transitionMode: e.target.value,
            ownerId: this.production.owner,
            production_id: this.production._id,
          },
          {
            ownerId: this.production.owner,
            userId: this.$store.state.user.id,
            isStaff: this.$store.getters.isStaff,
          },
      );
    },

    setTransition(e) {
      this.$store.dispatch('sendSettings');
      this.$store.dispatch('sendPreviewSettings');

      ConduitEventsService.track(
          'producer.changed.transition',
          {
            transition: e.target.value,
            ownerId: this.production.owner,
            production_id: this.production._id,
          },
          {
            ownerId: this.production.owner,
            userId: this.$store.state.user.id,
            isStaff: this.$store.getters.isStaff,
          },
      );
    },

    onChangeFlags() {
      this.showSaveButton = this.$ld.has('producer-save-production-button', false);
    },

    refresh() {
      window.location.reload();
    },

    openEditing() {
      this.editing = true;

      this.$nextTick(() => {
        this.$refs.title.focus();
      });
    },

    usePresetDimensions(v) {
      if (!v.width) {
        this.useCustomDimensions = true;
        return;
      }

      const { width, height } = v;
      this.useCustomDimensions = false;
      this.updateDimensions({
        ...this.production,
        xDimension: width,
        yDimension: height,
      });
    },

    setBackgroundColor(color) {
      this.pendingBackgroundColor = color;
    },

    saveBackgroundColor(color) {
      const backgroundColor = color || this.pendingBackgroundColor;

      if (!backgroundColor) {
        return;
      }

      this.$set(this.production, 'backgroundColor', backgroundColor);
      this.pendingBackgroundColor = null;
      this.sendSettings();
      this.sendPreviewSettings();

      ConduitEventsService.track(
          'producer.changed.background-color',
          {
            production_id: this.production._id,
            ownerId: this.production.owner,
          },
          {
            ownerId: this.production.owner,
            userId: this.$store.state.user.id,
            isStaff: this.$store.getters.isStaff,
          },
      );
    },

    cancelBackgroundColor() {
      this.pendingBackgroundColor = null;
    },

    selectAsset() {
      this.$store.dispatch('toggleAssetModal', { toggled: true, type: this.productionBackgroundType });

      window.assetmanfn = async (asset) => {
        if (this.productionBackgroundType === 'video') {
          await this.setBackgroundAsset({
            url: asset.media_url,
            poster: asset.thumbnail_url,
            type: asset.meta.content_type,
          });
        } else {
          await this.setBackgroundAsset(asset.media_url);
        }

        this.$store.dispatch('toggleAssetModal', { toggled: false, type: this.productionBackgroundType });
        this.sendSettings();
        this.sendPreviewSettings();
      };
    },
  },
  computed: {
    ...mapState({
      production: (state) => state.production.production,
      xDimension: (state) => state.production.production.xDimension,
      yDimension: (state) => state.production.production.yDimension,
      backgroundType: (state) => state.production.production.backgroundType,
      backgroundColor: (state) => state.production.production.backgroundColor ?? 'rgba(0,0,0,0)',
      isSaving: (state) => state.production.saving,
    }),

    production() {
      return this.$store.state.production.production;
    },

    // Used for graphic transitions
    transitionMode: {
      get() {
        return this.production.transitionMode || 'out-in';
      },

      set(transitionMode) {
        this.$set(this.production, 'transitionMode', transitionMode);
      },
    },

    // Used for theme transitions
    transition: {
      get() {
        return this.production.transition || 'animated cutIn';
      },

      set(transition) {
        this.$set(this.production, 'transition', transition);
      },
    },

    connectedUsers() {
      // this whole state management socket thing for clients needs to be refactored
      // which was a little big for this bug fix ticket, so I left in this loop thing
      // and just made it get loop in all, so I could filter out dupes in the component
      const clients = this.$store.state.clients.userOrder;
      const connectedUsers = [];

      for (let i = 0; i < clients.length; i += 1) {
        if (clients[i]) {
          connectedUsers.push(this.$store.state.clients.connectedUsers[clients[i]]);
        }
      }

      return connectedUsers;
    },

    disableSizeInputs() {
      return !this.useCustomDimensions || this.selectedScreenSize !== 'Custom';
    },
    iframeWidth: {
      get() {
        return this.xDimension;
      },
      set(value) {
        this.updateDimensions({
          ...this.production,
          xDimension: value,
        });
      },
    },
    iframeHeight: {
      get() {
        return this.yDimension;
      },
      set(value) {
        this.updateDimensions({
          ...this.production,
          yDimension: value,
        });
      },
    },

    productionBackgroundType: {
      get() {
        return this.backgroundType ?? 'color';
      },

      set(value) {
        this.setBackgroundType(value);

        if (value === 'image' && !this.production.backgroundImage) {
          this.selectAsset();
        } else if (value === 'video' && !this.production.backgroundVideo?.url) {
          this.selectAsset();
        } else {
          this.sendSettings();
          this.sendPreviewSettings();
        }
      },
    },

    productionBackgroundMedia() {
      const mediaUrl = this.productionBackgroundType === 'video'
        ? this.production.backgroundVideo?.poster
        : this.production.backgroundImage;
      const thumbnailUrl = getResizedImageUrl(mediaUrl, 'w', 100);

      return `url(${thumbnailUrl}), url(${process.env.ASSET_CDN_BASE}/placeholders/image-not-available.png)`;
    },

    selectedScreenSize() {
      if (this.useCustomDimensions) return 'Custom';

      const preset = this.presetDimensions.find((size) => size.width === this.xDimension && size.height === this.yDimension);

      return preset ? preset.short : 'Custom';
    },
  },
};
</script>

<style lang="scss">
.screen-size {
  &__input {
    width: 5em;
    font-size: 0.8125rem;
    line-height: 1.5;

    &:disabled {
      cursor: not-allowed;
      color: #A5A5A5;
    }
  }

  &__dropdown {
    border: 1px solid variables.$color-outline;

    .dropdown-menu {
      width: auto;
    }
  }

  &__options-list {
    list-style: none;
    margin: 0;
    padding: 0;

    .tb-btn-text {
      text-transform: none;
    }
  }
}

.window-background {
  .sp-replacer {
    height: 1.5rem !important;
    padding: 0 !important;
  }
}

.backgroundDropdown {
  border: 1px solid variables.$color-outline;
  border-radius: 2px;

  .dropdown-menu {
    width: auto;
  }
}

.screen-size__dropdown {
  border-radius: 2px;

  .header-section.tb-btn-text {
    border-radius: 2px;
  }
}

.outputBackgroundColor {
  margin-right: 2px;
  display: block;
  width: 2rem;
  height: 1.5rem;
  flex: 0 0 2rem;

  .colorpicker {
    display: block;
    width: 100%;
    height: 100%;
  }

  .colorpicker__trigger {
    width: 100%;
    min-width: 0;
    height: 1.5rem;
    display: block;
    border: none;
    background: transparent;
    padding: 0;
  }

  .colorpicker__value {
    display: none;
  }

  .colorpicker__swatch {
    width: 100%;
    height: 100%;
    border-radius: 2px;
    display: block;
  }

  .sp-replacer {
    height: 1.5rem !important;
    padding: 0 !important;
  }
}

.outputBackground {
  position: relative;
  height: 1.625rem;
  width: 2rem;
  margin-right: 2px;

  &__image {
    height: 100%;
    width: 100%;
    background-size: contain;
    background-position: center;
    background-repeat: no-repeat;
    border-radius: 2px;
  }

  &__edit {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    opacity: 0;
    transition: opacity 0.2s;
    padding: 0;
    width: 100%;
    height: 100%;
    border-radius: 0;
  }

  &:hover > &__edit {
    opacity: 1;
  }
}
</style>
